import { TranslateService } from "@ngx-translate/core";

import { Injectable, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { DialogMessageComponent } from "../components/dialogs/dialog-message/dialog-message";

export interface ConfirmationCheck {
  name: string;
  label: string;
  initialValue?: boolean;
}

export interface ConfirmationData {
  title: string;
  message: string;
  checks?: ConfirmationCheck[];
}

// these are returned via the callback.
// Just the name of the property and whether it was checked or not
export interface ConfirmationCheckResult {
  name: string;
  value: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ConfirmationDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);

  protected readonly title = signal(this.translate.instant("Global.Confirm.Title"));
  protected readonly message = signal(this.translate.instant("Global.Confirm.Message"));
  protected readonly checks = signal<ConfirmationCheck[]>([]);

  /**
   * Show a confirmation dialog with custom title and message
   *
   * @deprecated use requestConfirmation instead
   * @param callback function to be called on positive feedback
   * @param data An object containing title and message
   */
  public confirmWithData(callback: (checkboxResults?: ConfirmationCheckResult[]) => void, data: ConfirmationData) {
    this.title.set(this.translate.instant(data.title));
    this.message.set(this.translate.instant(data.message));
    this.checks.set(data.checks ?? []);

    this.confirm(callback);
  }

  /**
   * Request confirmation from the user. Optionally supply one or more
   * checkboxes for the user to check or uncheck.
   * The promise resolves when the user clicks 'OK'.
   *
   * @param data Configuration for the confirmation dialos
   * @returns a list of checkboxes and their result (if applicable)
   */
  public requestConfirmation(data: ConfirmationData): Promise<ConfirmationCheckResult[]> {
    this.title.set(this.translate.instant(data.title));
    this.message.set(this.translate.instant(data.message));
    this.checks.set(data.checks ?? []);

    return new Promise((resolve, reject) => {
      // open the dialog
      const dialogRef = this.dialog.open(DialogMessageComponent, {
        data: {
          title: this.title(),
          message: this.message(),
          isConfirm: true,
          checks: this.checks(),
        },
      });

      // when the 'OK' button is pressed
      dialogRef.componentInstance.emitter.subscribe((bool) => {
        if (bool) {
          const checks = dialogRef.componentInstance.getCheckboxResults();
          resolve(checks);
        } else {
          reject();
        }
      });
    });
  }

  /**
   * Show a default confirmation dialog
   *
   * @param callback function to be called on positive feedback
   */
  public confirm(callback: (checkboxResults?: ConfirmationCheckResult[]) => void): void {
    const dialogRef = this.dialog.open(DialogMessageComponent, {
      data: {
        title: this.title(),
        message: this.message(),
        isConfirm: true,
        checks: this.checks(),
      },
    });
    dialogRef.componentInstance.emitter.subscribe((bool) => {
      if (bool) {
        const checks = dialogRef.componentInstance.getCheckboxResults();
        callback(checks);
      }
    });
  }
}
